import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ConfigurationManagerService, BaseMultiLanguageConfiguration } from './ConfigurationManagerService';
import { HistoryManagerService } from './HistoryManagerService';
import { hasEquivalentBusinessState } from 'src/app/helpers/configuration-state-normalizer';

/**
 * Interface représentant l'état de la sauvegarde automatique
 */
export interface AutoSaveStatus {
  /** Indique si la sauvegarde automatique est activée */
  isActive: boolean;
  /** Timestamp de la dernière sauvegarde réussie */
  lastSaveTime: number | null;
  /** Indique si une sauvegarde est actuellement en cours */
  saveInProgress: boolean;
  /** Message d'erreur de la dernière sauvegarde échouée */
  saveError: string | null;
  /** Délai en millisecondes avant déclenchement de la sauvegarde */
  debounceDelay: number;
  /** Nombre total de sauvegardes effectuées */
  totalSaves: number;
}

/**
 * Type de la fonction de sauvegarde injectable.
 * Permet de remplacer la sauvegarde simulée par un appel backend réel.
 */
export type SaveFunction = (config: BaseMultiLanguageConfiguration) => Promise<void>;
interface SerializedSaveOptions {
  description?: string;
  historyType?: 'auto' | 'manual' | 'batch';
  synchronizeLanguagesBeforeSave?: boolean;
  throwIfMissingConfig?: boolean;
  source: 'auto' | 'manual' | 'batch' | 'history';
}

/**
 * Service gérant la sauvegarde automatique des configurations multilingues
 * avec protection contre les sauvegardes infinies lors des opérations d'historique.
 *
 * Fonctionnalités :
 * - Debounce configurable sur les changements de configuration
 * - Protection anti-boucles lors des opérations d'historique (undo/redo)
 * - Mode batch pour les opérations multi-champs (un seul snapshot + une seule sauvegarde)
 * - Fonction de sauvegarde injectable (backend réel ou simulé)
 * - Synchronisation automatique des langues après chaque sauvegarde
 */
@Injectable({
  providedIn: 'root'
})
export class AutoSaveDemoService implements OnDestroy {
  /** BehaviorSubject contenant l'état actuel de la sauvegarde automatique */
  private statusSubject = new BehaviorSubject<AutoSaveStatus>({
    isActive: false,
    lastSaveTime: null,
    saveInProgress: false,
    saveError: null,
    debounceDelay: 800,
    totalSaves: 0
  });

  /** Souscription active à l'écoute des changements de configuration */
  private configurationSubscription?: Subscription;
  
  /** Flag de suppression temporaire de la prochaine sauvegarde automatique */
  private suppressNextAutoSave = false;

  /** Flag indiquant qu'une opération batch est en cours */
  private _isBatchSave = false;

  /** Fonction de sauvegarde injectable (par défaut : simulation) */
  private _saveFn: SaveFunction | null = null;

  /** Compteur de sauvegardes en attente pendant un batch */
  private _pendingBatchSave = false;

  /**
   * Indique si la configuration courante diffère de la dernière version
   * sauvegardée (modifications utilisateur non encore persistées).
   * Mis à jour automatiquement par le listener de configuration.
   */
  private _hasPendingChangesSubject = new BehaviorSubject<boolean>(false);

  /**
   * Snapshot de la dernière configuration considérée comme "propre" côté
   * persistance. Sert de baseline pour distinguer une vraie modification
   * métier d'une simple navigation / relecture UI.
   */
  private _cleanConfigBaseline: BaseMultiLanguageConfiguration | null = null;

  /**
   * Propriétaire (ex: orderId) de la configuration au moment où la session
   * d'auto-save courante a démarré. Garde anti-fuite inter-commandes : si le
   * propriétaire de la config change en cours de route (navigation vers une
   * autre commande, second onglet, réutilisation de composant), on refuse de
   * persister pour éviter qu'une commande écrase la configuration d'une autre.
   * `undefined` = aucune session capturée (garde inactive, rétro-compat).
   */
  private _sessionOwnerContextId: string | number | null | undefined = undefined;

  /** File sérialisée des sauvegardes pour éviter les écritures concurrentes. */
  private _saveQueue: Promise<void> = Promise.resolve();

  /** Nombre de sauvegardes actives ou en attente dans la file. */
  private _queuedSaveCount = 0;

  /** Nombre maximum de tentatives pour rattraper un état qui bouge pendant un save. */
  private readonly MAX_SAVE_STABILIZATION_ATTEMPTS = 10;

  constructor(
    private configManager: ConfigurationManagerService,
    private historyManager: HistoryManagerService
  ) {
    this.setupConfigurationListener();
    this.setupPendingChangesTracker();
  }

  // ─── Observables & Getters ────────────────────────────────

  /**
   * Observable de l'état de sauvegarde automatique (alias saveStatus$)
   */
  get saveStatus$(): Observable<AutoSaveStatus> {
    return this.statusSubject.asObservable();
  }

  /**
   * Observable de l'état de sauvegarde automatique (alias historique)
   */
  get autoSaveStatus$(): Observable<AutoSaveStatus> {
    return this.saveStatus$;
  }

  /**
   * État actuel de la sauvegarde automatique
   */
  get autoSaveStatus(): AutoSaveStatus {
    return this.statusSubject.value;
  }

  /**
   * Délai de debounce actuel en millisecondes
   */
  get debounceDelay(): number {
    return this.statusSubject.value.debounceDelay;
  }

  /**
   * Indique si une opération batch est en cours
   */
  get isBatchSave(): boolean {
    return this._isBatchSave;
  }

  /**
   * Observable indiquant si la config courante a des modifications non encore
   * persistées (`true` dès qu'une modif utilisateur arrive, `false` après
   * succès d'une sauvegarde). Inclut aussi `saveInProgress` pour couvrir la
   * fenêtre où la sauvegarde est en cours mais pas encore persistée côté backend.
   */
  get hasPendingChanges$(): Observable<boolean> {
    return this._hasPendingChangesSubject.asObservable();
  }

  /**
   * Snapshot synchrone : `true` si la config courante a des modifications non
   * encore persistées ou si une sauvegarde est en cours.
   */
  get hasPendingChanges(): boolean {
    return this._hasPendingChangesSubject.value || this.autoSaveStatus.saveInProgress;
  }

  // ─── Configuration de la fonction de sauvegarde ───────────

  /**
   * Définit la fonction de sauvegarde backend.
   * Si non définie, une simulation est utilisée.
   * @param fn Fonction de sauvegarde asynchrone
   */
  setSaveFn(fn: SaveFunction): void {
    this._saveFn = fn;
  }

  // ─── Démarrage / Arrêt ────────────────────────────────────

  /**
   * Démarre la sauvegarde automatique avec un délai de debounce spécifié
   * @param debounceDelay Délai en millisecondes avant déclenchement de la sauvegarde
   */
  async startAutoSave(debounceDelay: number = 1000): Promise<void> {
    this.stopAutoSave();

    this.updateStatus({
      isActive: true,
      saveError: null,
      debounceDelay: debounceDelay
    });

    // Au démarrage de l'auto-save, la configuration déjà chargée devient la
    // baseline "propre" : ouvrir le configurateur ne doit pas créer d'état dirty.
    // On mémorise aussi le propriétaire (orderId) de cette config : toute
    // persistance ultérieure sera refusée si ce propriétaire change.
    this._sessionOwnerContextId = this.configManager.ownerContextId;
    this.captureCleanConfigBaseline(this.configManager.currentConfiguration);
    this._hasPendingChangesSubject.next(false);

    // Ignorer toute émission déjà en pipeline (antérieure à l'activation).
    // La config émise pendant initializeConfiguration() peut arriver après le debounce
    // alors que isActive est déjà true — on supprime cette fenêtre.
    this.suppressNextAutoSave = true;
    setTimeout(() => {
      this.suppressNextAutoSave = false;
      // Fin de la fenêtre d'ouverture : les réconciliations automatiques déclenchées
      // au montage des éditeurs (ex: synchronisation de la précédence RiddleHunt selon
      // le type de parcours, normalisation SimpleQuiz) ont pu muter la configuration
      // APRÈS la capture de la baseline initiale. On ré-ancre la baseline sur l'état
      // courant pour que ces ajustements d'ouverture ne soient pas comptés comme des
      // « modifications en attente » (sinon `hasPendingChanges` reste bloqué à true et
      // la modale de confirmation se rouvre en boucle à chaque navigation).
      if (this.autoSaveStatus.isActive) {
        this.captureCleanConfigBaseline(this.configManager.currentConfiguration);
        if (this._hasPendingChangesSubject.value) {
          this._hasPendingChangesSubject.next(false);
        }
      }
    }, debounceDelay + 500);
  }

  /**
   * Arrête la sauvegarde automatique
   */
  stopAutoSave(): void {
    this.updateStatus({
      isActive: false
    });

    console.log('Sauvegarde automatique désactivée');
  }

  // ─── Listener de configuration ────────────────────────────

  /**
   * Marque la config comme « modifiée non sauvegardée » dès qu'une émission
   * provient d'une vraie modification utilisateur (pas un undo/redo, pas une
   * synchronisation interne post-save). Sans debounce : on veut couvrir aussi
   * la fenêtre [modif → debounceDelay → save] pendant laquelle l'utilisateur
   * pourrait quitter la page.
   */
  private setupPendingChangesTracker(): void {
    this.configManager.currentConfigurationObservable.subscribe((config) => {
      if (!config) return;
      if (!this.autoSaveStatus.isActive) {
        this.captureCleanConfigBaseline(config);
        return;
      }
      if (this.configManager.isHistoryOperation) return;
      if (this.suppressNextAutoSave) return;

      const hasRealPendingChanges = this._cleanConfigBaseline
        ? !hasEquivalentBusinessState(this._cleanConfigBaseline, config)
        : false;

      if (this._hasPendingChangesSubject.value !== hasRealPendingChanges) {
        this._hasPendingChangesSubject.next(hasRealPendingChanges);
      }
    });
  }

  /** Marque la config courante comme entièrement persistée (à appeler après save success). */
  private markPendingChangesResolved(config: BaseMultiLanguageConfiguration | null = null): void {
    const resolvedConfig = config ?? this.configManager.currentConfiguration;
    this.captureCleanConfigBaseline(resolvedConfig);

    const currentConfig = this.configManager.currentConfiguration;
    const hasRealPendingChanges = resolvedConfig && currentConfig
      ? !hasEquivalentBusinessState(resolvedConfig, currentConfig)
      : false;

    if (this._hasPendingChangesSubject.value !== hasRealPendingChanges) {
      this._hasPendingChangesSubject.next(hasRealPendingChanges);
    }
  }

  /** Capture un snapshot isolé de la baseline "propre" de comparaison. */
  private captureCleanConfigBaseline(config: BaseMultiLanguageConfiguration | null): void {
    this._cleanConfigBaseline = config
      ? JSON.parse(JSON.stringify(config)) as BaseMultiLanguageConfiguration
      : null;
  }

  /**
   * Configure l'écoute des changements de configuration avec protection anti-boucles.
   * Utilise debounceTime et distinctUntilChanged pour éviter les sauvegardes excessives.
   */
  private setupConfigurationListener(): void {
    this.configurationSubscription = this.configManager.currentConfigurationObservable
      .pipe(
        debounceTime(this.debounceDelay),
        distinctUntilChanged((prev, curr) => {
          if (!prev || !curr) return false;
          const isHistoryOp = this.configManager.isHistoryOperation;
          const equivalent = isHistoryOp || hasEquivalentBusinessState(prev, curr);

          this.debugLog('listener-compare', {
            isHistoryOp,
            equivalent,
            prevLastModified: prev.lastModified,
            currLastModified: curr.lastModified,
            prevSummary: this.buildCodesScannerSummary(prev),
            currSummary: this.buildCodesScannerSummary(curr),
          });

          return equivalent;
        })
      )
      .subscribe((config) => {
        this.debugLog('listener-emission', {
          isActive: this.autoSaveStatus.isActive,
          suppressNextAutoSave: this.suppressNextAutoSave,
          isHistoryOperation: this.configManager.isHistoryOperation,
          summary: this.buildCodesScannerSummary(config),
        });

        if (!config || !this.autoSaveStatus.isActive) return;

        if (this.suppressNextAutoSave) {
          this.debugLog('listener-skip-suppressed');
          return;
        }
        if (this.configManager.isHistoryOperation) {
          this.debugLog('listener-skip-history-operation');
          return;
        }

        if (this._isBatchSave) {
          this._pendingBatchSave = true;
          this.debugLog('listener-skip-batch-save-pending');
          return;
        }

        this.debugLog('listener-trigger-autosave');
        this.performAutoSave();
      });
  }

  // ─── Sauvegarde automatique ───────────────────────────────

  /**
   * Effectue la sauvegarde automatique de la configuration actuelle.
   * Gère les erreurs, met à jour l'historique après succès, et synchronise les langues.
   * @param description Description optionnelle pour l'entrée d'historique
   */
  private async performAutoSave(description?: string): Promise<void> {
    try {
      await this.runSerializedSave({
        source: 'auto',
        description,
        historyType: 'auto',
        synchronizeLanguagesBeforeSave: true,
      });
    } catch (error) {
      this.debugLog('perform-auto-save-error', {
        error: error instanceof Error ? error.message : error,
      });

      console.error('Erreur lors de la sauvegarde:', error);
    }
  }

  /**
   * Exécute la fonction de sauvegarde (injectable ou simulée)
   */
  private async executeSave(config: BaseMultiLanguageConfiguration): Promise<void> {
    if (this._saveFn) {
      await this._saveFn(config);
    } else {
      // Simulation par défaut (à remplacer par setSaveFn en production)
      await new Promise<void>(resolve => setTimeout(resolve, 300));
    }
  }

  // ─── Opérations batch ─────────────────────────────────────

  /**
   * Exécute une opération batch : toutes les modifications intermédiaires sont groupées
   * en une seule sauvegarde et un seul snapshot d'historique.
   *
   * Utilise ConfigurationManagerService.batchOperation() pour buffer les émissions,
   * puis effectue une sauvegarde unique à la fin.
   *
   * @param operation Fonction contenant les modifications à grouper
   * @param description Description pour l'entrée d'historique
   */
  async batchSave(operation: () => void | Promise<void>, description?: string): Promise<void> {
    if (this._isBatchSave) {
      // Si déjà dans un batch, exécuter directement (nested batch)
      await operation();
      return;
    }

    this._isBatchSave = true;
    this._pendingBatchSave = false;

    try {
      // Utiliser le batchOperation du ConfigurationManager pour buffer les émissions
      this.configManager.batchOperation(() => {
        const result = operation();
        // Si l'opération retourne une Promise, on ne peut pas l'await dans le sync batchOperation
        // Pour les opérations async, utiliser batchSaveAsync
        if (result instanceof Promise) {
          console.warn('batchSave: opération async détectée, utiliser batchSaveAsync pour les opérations asynchrones');
        }
      });
    } finally {
      this._isBatchSave = false;
    }

    await this.runSerializedSave({
      source: 'batch',
      description: description || 'Opération groupée',
      historyType: 'batch',
      synchronizeLanguagesBeforeSave: true,
    });

    this._pendingBatchSave = false;
  }

  // ─── Sauvegarde manuelle ──────────────────────────────────

  /**
   * Effectue une sauvegarde manuelle immédiate de la configuration actuelle
   * @param description Description optionnelle pour l'historique
   */
  async manualSave(description?: string): Promise<void> {
    await this.runSerializedSave({
      source: 'manual',
      description: description || 'Sauvegarde manuelle',
      historyType: 'manual',
      synchronizeLanguagesBeforeSave: true,
      throwIfMissingConfig: true,
    });

    console.log('Sauvegarde manuelle réussie');
  }

  /**
   * Effectue une sauvegarde sans ajouter à l'historique.
   * Utilisé pour les opérations d'historique (undo/redo).
   */
  async saveWithoutHistory(): Promise<void> {
    await this.runSerializedSave({
      source: 'history',
      throwIfMissingConfig: true,
    });

    console.log('Sauvegarde (historique) réussie');
  }

  // ─── Synchronisation des langues ──────────────────────────

  /**
   * Synchronise les champs structurels de la langue principale vers les autres langues.
   * Utilise les méthodes dédiées du ConfigurationManagerService.
   */
  private synchronizeLanguages(config: BaseMultiLanguageConfiguration): void {
    const primaryLang = config.primaryLanguage;
    const languages = this.configManager.availableLanguages;

    if (languages.length <= 1) return;

    for (const lang of languages) {
      if (lang === primaryLang) continue;

      // Sync champs structurels via ConfigurationManagerService
      this.configManager.syncStructuralFields(primaryLang, lang);
    }
  }

  private synchronizeLanguagesBeforeSave(): void {
    const currentConfig = this.configManager.currentConfiguration;
    if (!currentConfig || this.configManager.availableLanguages.length <= 1) {
      return;
    }

    const wasSuppressed = this.suppressNextAutoSave;
    this.suppressNextAutoSave = true;

    try {
      this.configManager.batchOperation(() => {
        this.synchronizeLanguages(currentConfig);
      });
    } finally {
      this.suppressNextAutoSave = wasSuppressed;
    }
  }

  // ─── Contrôle de la suppression ───────────────────────────

  /**
   * Modifie le délai de debounce pour la sauvegarde automatique
   * @param milliseconds Nouveau délai en millisecondes
   */
  setDebounceDelay(milliseconds: number): void {
    this.updateStatus({ debounceDelay: milliseconds });
    
    if (this.autoSaveStatus.isActive) {
      this.startAutoSave(milliseconds);
    }
  }

  /**
   * Désactive temporairement la sauvegarde automatique.
   * Utilisé lors des opérations d'historique (undo/redo).
   */
  suppressAutoSave(): void {
    this.suppressNextAutoSave = true;
  }

  /**
   * Réactive la sauvegarde automatique
   */
  resumeAutoSave(): void {
    this.suppressNextAutoSave = false;
  }

  /**
   * Exécute une fonction en désactivant temporairement la sauvegarde automatique
   * @param fn Fonction à exécuter avec sauvegarde désactivée
   * @returns Résultat de la fonction exécutée
   */
  withSuppressedAutoSave<T>(fn: () => T): T {
    const wasAlreadySuppressed = this.suppressNextAutoSave;
    this.suppressAutoSave();
    try {
      return fn();
    } finally {
      if (!wasAlreadySuppressed) {
        setTimeout(() => this.resumeAutoSave(), 500);
      }
    }
  }

  private buildCodesScannerSummary(config: BaseMultiLanguageConfiguration | null): Record<string, unknown> {
    const summary: Record<string, unknown> = {};

    for (const [language, baseConfig] of Object.entries(config?.baseConfigurations || {})) {
      summary[language] = Object.entries(baseConfig?.games || {})
        .filter(([, game]) => game?.type === 'CodesScanner')
        .map(([gameKey, game]) => ({
          gameKey,
          codesCount: Array.isArray((game as any)?.data?.codes) ? (game as any).data.codes.length : null,
          actionIds: Array.isArray((game as any)?.data?.codes)
            ? (game as any).data.codes.map((code: any) => code?.actionId ?? null)
            : [],
        }));
    }

    return summary;
  }

  private debugLog(step: string, payload: Record<string, unknown> = {}): void {
    console.log('[CodesScannerDebug][AutoSave]', step, payload);
  }
  private async runSerializedSave(options: SerializedSaveOptions): Promise<BaseMultiLanguageConfiguration | null> {
    return this.enqueueSave(async () => {
      for (let attempt = 1; attempt <= this.MAX_SAVE_STABILIZATION_ATTEMPTS; attempt++) {
        // Garde anti-fuite inter-commandes : si le propriétaire de la config
        // courante n'est plus celui pour lequel la session d'auto-save a démarré,
        // on abandonne la sauvegarde. Évite qu'une navigation / un second onglet
        // fasse persister la config d'une commande dans une autre.
        if (!this.isSessionOwnerContextValid()) {
          this.debugLog('serialized-save-skip-owner-changed', {
            source: options.source,
            attempt,
            sessionOwner: this._sessionOwnerContextId,
            currentOwner: this.configManager.ownerContextId,
          });
          return null;
        }

        const configToSave = this.getConfigSnapshotForSave(options);
        if (!configToSave) {
          if (options.throwIfMissingConfig) {
            throw new Error('Aucune configuration à sauvegarder');
          }
          return null;
        }

        if (this.isConfigAlreadyPersisted(configToSave)) {
          this.debugLog('serialized-save-skip-already-persisted', {
            source: options.source,
            attempt,
            summary: this.buildCodesScannerSummary(configToSave),
          });
          this.markPendingChangesResolved(configToSave);
          return configToSave;
        }

        this.debugLog('serialized-save-attempt', {
          source: options.source,
          attempt,
          description: options.description ?? null,
          summary: this.buildCodesScannerSummary(configToSave),
        });

        await this.executeSave(configToSave);
        this.recordSuccessfulSaveAttempt();

        if (!this.isCurrentConfigurationEquivalent(configToSave)) {
          this.debugLog('serialized-save-stale-snapshot', {
            source: options.source,
            attempt,
            summary: this.buildCodesScannerSummary(configToSave),
            currentSummary: this.buildCodesScannerSummary(this.configManager.currentConfiguration),
          });
          continue;
        }

        if (options.historyType) {
          this.historyManager.addToHistory(
            configToSave,
            options.description,
            options.historyType,
          );
        }

        this.markPendingChangesResolved(configToSave);

        this.debugLog('serialized-save-success', {
          source: options.source,
          attempt,
          totalSaves: this.autoSaveStatus.totalSaves,
          summary: this.buildCodesScannerSummary(configToSave),
        });

        console.log('Sauvegarde réussie', {
          source: options.source,
          timestamp: new Date().toLocaleTimeString(),
          total: this.autoSaveStatus.totalSaves,
        });

        return configToSave;
      }

      throw new Error(`La sauvegarde n'a pas pu se stabiliser après ${this.MAX_SAVE_STABILIZATION_ATTEMPTS} tentatives`);
    });
  }

  private getConfigSnapshotForSave(options: SerializedSaveOptions): BaseMultiLanguageConfiguration | null {
    if (options.synchronizeLanguagesBeforeSave) {
      this.synchronizeLanguagesBeforeSave();
    }

    const currentConfig = this.configManager.currentConfiguration;
    if (!currentConfig) {
      return null;
    }

    return this.cloneConfiguration(currentConfig);
  }

  private enqueueSave<T>(task: () => Promise<T>): Promise<T> {
    this._queuedSaveCount++;

    if (!this.autoSaveStatus.saveInProgress) {
      this.updateStatus({
        saveInProgress: true,
        saveError: null,
      });
    }

    const runTask = async (): Promise<T> => {
      try {
        return await task();
      } catch (error) {
        this.updateStatus({
          saveError: error instanceof Error ? error.message : 'Erreur de sauvegarde inconnue',
        });
        throw error;
      } finally {
        this._queuedSaveCount--;

        if (this._queuedSaveCount === 0) {
          this.updateStatus({
            saveInProgress: false,
          });
        }
      }
    };

    const result = this._saveQueue.then(runTask, runTask);
    this._saveQueue = result.then(() => undefined, () => undefined);

    return result;
  }

  private recordSuccessfulSaveAttempt(): void {
    this.updateStatus({
      lastSaveTime: Date.now(),
      saveError: null,
      totalSaves: this.autoSaveStatus.totalSaves + 1,
    });
  }

  private isCurrentConfigurationEquivalent(config: BaseMultiLanguageConfiguration): boolean {
    const currentConfig = this.configManager.currentConfiguration;
    return currentConfig ? hasEquivalentBusinessState(config, currentConfig) : false;
  }

  private isConfigAlreadyPersisted(config: BaseMultiLanguageConfiguration): boolean {
    return this._cleanConfigBaseline
      ? hasEquivalentBusinessState(this._cleanConfigBaseline, config)
      : false;
  }

  /**
   * Vérifie que la configuration courante appartient toujours à la commande
   * pour laquelle la session d'auto-save a démarré. Empêche qu'une bascule vers
   * une autre commande (navigation, second onglet) fasse écrire la config d'une
   * commande dans une autre. Retourne `true` si aucune session n'a été capturée.
   */
  private isSessionOwnerContextValid(): boolean {
    if (this._sessionOwnerContextId === undefined) return true;
    return this.configManager.ownerContextId === this._sessionOwnerContextId;
  }

  private cloneConfiguration(config: BaseMultiLanguageConfiguration): BaseMultiLanguageConfiguration {
    return JSON.parse(JSON.stringify(config)) as BaseMultiLanguageConfiguration;
  }

  // ─── Utilitaires privés ───────────────────────────────────

  /**
   * Met à jour l'état de la sauvegarde automatique
   */
  private updateStatus(updates: Partial<AutoSaveStatus>): void {
    const current = this.autoSaveStatus;
    this.statusSubject.next({ ...current, ...updates });
  }

  /**
   * Nettoyage des ressources lors de la destruction du service
   */
  ngOnDestroy(): void {
    this.stopAutoSave();
    if (this.configurationSubscription) {
      this.configurationSubscription.unsubscribe();
    }
  }
}